import { memo, useEffect, useCallback, useState, useRef } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownIcon from '@mui/icons-material/ArrowDownward';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import PropTypes from 'prop-types';

import { useVisit } from '@zenbusiness/application-commons-visit';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Divider from '@zenbusiness/zui/Divider';
import Text from '@zenbusiness/zui/Text';

import { useUnifiedBusinessEntityContext } from '../../contexts/UnifiedBusinessEntityProvider';
import { useInlineConversation } from '../../hooks/useInlineConversation';
import useUserMode from '../../hooks/useUserMode';
import ActivePromptInput from '../ActivePromptInput';
import ChatErrorMessage from '../ChatErrorMessage';
import DynamicRenderer from '../DynamicRenderer';
import * as Styled from './VeloQA.style';
import LegalFooter from '../LegalFooter';
import Logo from '../Logo';
import { VELO_QA_CONFIGS, ComponentTypes, CLICKSTREAM_COMMON_PROPS } from '../utils/constants';
/**
 * Filter instructions to only show allowed component types
 * Removes ButtonPrompt and other unwanted components
 * @param instructions
 * @param allowedTypes
 */
function filterInstructions(instructions, allowedTypes) {
  return instructions.map((instruction) => {
    if (!instruction.attributes?.uiInstructions) {
      return instruction;
    }

    return {
      ...instruction,
      attributes: {
        ...instruction.attributes,
        uiInstructions: instruction.attributes.uiInstructions.filter((uiInstruction) =>
          allowedTypes.includes(uiInstruction.componentType)
        )
      }
    };
  });
}

/**
 * TODO LIST (for future development)
 * onThreadCreated - Callback when thread is created
 * onResponseReceived - Callback when response is received
 * TODO - Add functionality to support feedback form, button prompt and article open.
 */

/**
 * VeloQA Component
 *
 * A self-contained conversational card component that:
 * - Displays Velo branding, title, subtitle, and prompt chips
 * - Handles user input via ActivePromptInput
 * - Creates threads and sends messages using useInlineConversation
 * - Renders responses inline using DynamicRenderer
 * - Shows loading states and error handling
 * - Optionally displays CTA footer with thread link
 *
 * @param {object} props - Component props
 * @param {string} props.configKey - Key to lookup configuration from VELO_QA_CONFIGS
 * @param {string} [props.title] - Optional title override
 * @param {string} [props.subtitle] - Optional subtitle override
 * @param {Array} [props.prompts] - Optional prompts override
 * @param {boolean} [props.disabled] - Whether the card is disabled
 * @param {boolean} [props.showCTA] - Whether to show CTA footer
 * @param {string} [props.ctaText] - CTA button text
 * @param {Function} [props.onThreadCreated] - Callback when thread is created
 * @param {Function} [props.onResponseReceived] - Callback when response is received
 * @param {Array} [props.allowedComponentTypes] - Array of allowed component types to render
 * @param {string} [props.tag] - Tag identifier for thread context. Pattern: DOMAIN.namespace.key (e.g., COMPLIANCE.businessLicenses.notPurchased)
 * @param {string} [props.origination] - Where the thread was initiated (e.g., zendash-sidebar)
 * @param {object} [props.initialState] - Initial state of the conversation { threadUuid, sourceComponent }
 * @param {Function} [props.onStateChange] - Callback when state changes
 * @param {Function} [props.onNavigateToChat] - Callback when "Dive deeper" is clicked
 * @param {Function} [props.onError] - Callback when an error occurs
 * @returns {React.ReactElement} The VeloQA component
 */
const VeloQA = memo(
  ({
    configKey,
    title,
    subtitle,
    prompts,
    disabled = false,
    showCTA = true,
    ctaText = 'Dive deeper with Velo™',
    allowedComponentTypes = [ComponentTypes.MESSAGE, ComponentTypes.SUPPORT_CARD],
    autoSubmitPrompts = true,
    tag,
    origination,
    initialState,
    onStateChange,
    onNavigateToChat,
    onError
  }) => {
    const config = VELO_QA_CONFIGS[configKey] || VELO_QA_CONFIGS[tag];
    const { sourceComponent = 'velo-qa' } = initialState || {};

    // Refs
    const activePromptInputRef = useRef(null);
    const lastMessageRef = useRef('');

    // Get identity for thread creation
    const { businessEntityUuid } = useUnifiedBusinessEntityContext();
    const { visitUuid } = useVisit();
    const userMode = useUserMode();

    // TODO - We can add support for exiting thread in useInlineConversation
    // LoadNextInstruction is used if there is a threadUUID in the instructions
    // const { loadNextInstruction } = useInstructionRenderer();

    // TODO - Add functionality to support feedback form, button prompt and article open.
    // Use inline conversation hook (combines thread creation + message sending)
    const {
      threadUuid: currentThreadUuid,
      instructions,
      loading: loadingNext,
      error: responseError,
      sendMessage,
      addUserResponse,
      reset: resetConversation,
      hasResetOnInit
    } = useInlineConversation({
      tag,
      origination,
      sourceComponent,
      initialState,
      onStateChange,
      onNavigateToChat,
      onError
    });

    // Local state
    const [hasInteracted, setHasInteracted] = useState(false);
    const [isFirstMessage, setIsFirstMessage] = useState(true);
    const [retryLoading, setRetryLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [needsExpansion, setNeedsExpansion] = useState(false);

    // Ref for measuring content height
    const responseContainerRef = useRef(null);

    // Reset hasInteracted when thread is cleared by the hook on init
    useEffect(() => {
      if (!hasResetOnInit) return;
      setHasInteracted(false);
    }, [hasResetOnInit]);

    // Send message if initial state threadUuid is provided
    useEffect(() => {
      if (hasInteracted || hasResetOnInit || !initialState?.threadUuid) return;

      const sendMessageOnInit = async () => {
        await sendMessage(null, 'text', userMode.mode, {
          visitUuid,
          userUuid: userMode.userUuid,
          businessEntityUuid
        });
      };

      setHasInteracted(true);
      sendMessageOnInit();
    }, [
      initialState,
      sendMessage,
      visitUuid,
      userMode,
      businessEntityUuid,
      hasInteracted,
      hasResetOnInit
    ]);

    const handleVeloQAContextPrep = () => {
      const navigationContext = {
        referrer: window.location.pathname,
        // TODO - Add section back in when we have a way to get the section from the page
        //  section: window.location.pathname || 'unknown',
        timestamp: Date.now(),
        clickstreamContext: {
          elementName: 'VeloQA_CTA',
          elementPosition: 'VeloQA_CTA'
        }
      };
      onNavigateToChat?.(currentThreadUuid);

      try {
        sessionStorage.setItem('velo_source_context', JSON.stringify(navigationContext));
      } catch (error) {
        console.warn('[Navigation] Failed to store context:', error);
        onError?.({ type: 'navigation_context_storage_failed', reason: error.message });
      }
    };

    // Focus input on mount
    useEffect(() => {
      if (activePromptInputRef?.current?.focus && !disabled) {
        requestAnimationFrame(() => {
          activePromptInputRef?.current?.focus();
        });
      }
    }, [disabled]);

    // Callback when thread is created
    useEffect(() => {
      if (currentThreadUuid && isFirstMessage) {
        setIsFirstMessage(false);
      }
    }, [currentThreadUuid, isFirstMessage]);

    /**
     * Handle message send
     * Creates thread on first message, sends to existing thread on follow-up
     */
    const handleMessageSend = useCallback(
      async (message) => {
        if (disabled || loadingNext) return;

        // Store message for retry
        lastMessageRef.current = message;

        // Mark as interacted
        setHasInteracted(true);

        // Add optimistic user message
        addUserResponse(message);

        try {
          // sendMessage handles BOTH:
          // 1. Creating thread (if first message)
          // 2. Sending to existing thread (if follow-up)
          await sendMessage(message, 'text', userMode.mode, {
            visitUuid,
            userUuid: userMode.userUuid,
            businessEntityUuid
          });

          // Clear input on success
          if (activePromptInputRef.current?.clearValue) {
            activePromptInputRef.current.clearValue();
          }
        } catch (err) {
          console.error('[VeloQA] Message send failed:', err);
          onError?.({ type: 'message_send_failed', reason: err.message });
        }
      },
      [
        disabled,
        loadingNext,
        sendMessage,
        visitUuid,
        userMode.userUuid,
        userMode.mode,
        businessEntityUuid,
        addUserResponse,
        onError
      ]
    );

    /**
     * Handle prompt chip click
     * Populates input with prompt text
     */
    const handlePromptSelect = useCallback(
      (promptText) => {
        if (disabled || loadingNext) return;

        if (activePromptInputRef.current?.setValue) {
          activePromptInputRef.current.setValue(promptText);
          // Focus after setting value
          requestAnimationFrame(() => {
            activePromptInputRef.current?.focus();
          });
        }

        // Auto-submit if enabled (default: true)
        if (autoSubmitPrompts) {
          handleMessageSend(promptText);
        }
      },
      [disabled, loadingNext, autoSubmitPrompts, handleMessageSend]
    );

    /**
     * Handle retry on error
     */
    const handleRetry = useCallback(async () => {
      const messageToRetry = lastMessageRef.current;
      if (!messageToRetry) return;

      setRetryLoading(true);
      try {
        await handleMessageSend(messageToRetry);
      } finally {
        setRetryLoading(false);
      }
    }, [handleMessageSend]);

    /**
     * Handle start new conversation / reset to initial state
     */
    const handleStartNew = useCallback(() => {
      // Reset hook state (clears threadUuid, instructions, error)
      resetConversation();

      // Reset local state
      setHasInteracted(false);
      setIsFirstMessage(true);
      setIsExpanded(false);
      setNeedsExpansion(false);
      lastMessageRef.current = '';

      // Clear input
      if (activePromptInputRef.current?.clearValue) {
        activePromptInputRef.current.clearValue();
      }

      // Focus input after reset
      requestAnimationFrame(() => {
        activePromptInputRef.current?.focus();
      });
    }, [resetConversation]);

    /**
     * Toggle expanded state
     */
    const toggleExpanded = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    const promptList = prompts || config?.prompts || [];
    const hasError = Boolean(responseError);
    const hasResponse = instructions.length > 0 && !hasError && !loadingNext;

    /**
     * Check if content needs expansion based on height
     */
    useEffect(() => {
      if (hasResponse && responseContainerRef.current) {
        const contentHeight = responseContainerRef.current.scrollHeight;
        const maxHeight = 250; // Match the maxHeight in styles
        setNeedsExpansion(contentHeight > maxHeight);
      }
    }, [hasResponse, instructions]);

    // Filter instructions to only show allowed component types
    const filteredInstructions = filterInstructions(instructions, allowedComponentTypes);

    if (!config) {
      console.warn(`[VeloQA] Config not found for key: ${configKey}`);
      return null;
    }

    return (
      <Styled.CardContainer>
        {hasResponse && currentThreadUuid && (
          <>
            <Styled.CTAHeader>
              <Button
                size="small"
                startIcon={<ArrowBackIcon fontSize="small" />}
                variant="outlined"
                onClick={handleStartNew}
              >
                Go Back
              </Button>
            </Styled.CTAHeader>
            <Divider />
          </>
        )}

        {/* Header */}
        {!hasResponse && (
          <Styled.HeaderSection>
            {/** TODO - Add a prop to control if the logo is visible or hidden */}
            <Styled.LogoContainer>
              <Logo alt="Zenbusiness Velo" height={38} src="/s2/logo/zb-velo-compact-color.svg" />
            </Styled.LogoContainer>

            <Styled.TitleContainer>
              <Text color="primary" fontWeight="600" size="md">
                {title || config.title}
              </Text>
              <Text color="secondary" mt={1} size="sm">
                {subtitle || config.subtitle}
              </Text>
            </Styled.TitleContainer>
          </Styled.HeaderSection>
        )}
        {/* Input - Hide after first response is received */}
        {/* TODO - Add a prop to control if the input is visible or hidden */}
        {!hasResponse && (
          <Styled.InputContainer>
            <ActivePromptInput
              ref={activePromptInputRef}
              disabled={disabled || loadingNext}
              isAnswering={loadingNext}
              placeholder="Ask Velo..."
              visibleFooter={false}
              visibleMoreIndicator={false}
              onSendMessage={handleMessageSend}
            />
          </Styled.InputContainer>
        )}

        {/* Prompt Chips - Hide after first interaction */}
        {/* TODO - It might be a better user experience if the questions were above the input box so user can read and decide which question to ask */}
        {!hasInteracted && (
          <Styled.PromptsContainer>
            {promptList.map((prompt) => (
              <Styled.PromptChip
                key={prompt.id}
                disabled={disabled || loadingNext}
                fullWidth={false}
                size="small"
                onClick={() => handlePromptSelect(prompt.text)}
              >
                {prompt.text}
              </Styled.PromptChip>
            ))}
          </Styled.PromptsContainer>
        )}

        {/* Answer Area */}
        {hasInteracted && (
          <Styled.AnswerArea isExpanded={isExpanded}>
            {/* TODO - Add a loading animation */}
            {loadingNext && <Text size="sm">Thinking...</Text>}

            {/* TODO - Add prop to control how error is handled. Support onError function */}
            {hasError && !loadingNext && (
              <ChatErrorMessage
                loading={retryLoading}
                onRetry={handleRetry}
                onStartNew={handleStartNew}
              />
            )}

            {hasResponse && (
              <>
                <Styled.ResponseContainer ref={responseContainerRef} isExpanded={isExpanded}>
                  <DynamicRenderer
                    currentMode="post"
                    fullWidth
                    instructions={filteredInstructions}
                  />
                </Styled.ResponseContainer>

                <Styled.ExpandButtonContainer>
                  {/* TODO - Move the expansion button a location agreed by design team */}
                  {needsExpansion && !isExpanded && (
                    <Styled.ExpandCollapseButton
                      endIcon={<ArrowDownIcon fontSize="small" />}
                      size="small"
                      variant="outlined"
                      onClick={toggleExpanded}
                    >
                      See full details
                    </Styled.ExpandCollapseButton>
                  )}

                  {needsExpansion && isExpanded && (
                    <Styled.ExpandCollapseButton
                      endIcon={<ArrowUpIcon fontSize="small" />}
                      size="small"
                      variant="outlined"
                      onClick={toggleExpanded}
                    >
                      Show less
                    </Styled.ExpandCollapseButton>
                  )}
                </Styled.ExpandButtonContainer>
              </>
            )}
          </Styled.AnswerArea>
        )}

        {/* CTA Footer - Show after answer received 
           TODO - Add a prop to control if the CTA open in same page, new page or (future) slide in component or modal
        */}

        {showCTA && hasResponse && currentThreadUuid && (
          <Styled.CTAFooter>
            <Styled.VeloButton
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: 'VeloQA_CTA',
                elementText: ctaText
              }}
              endIcon={<ArrowOutwardIcon fontSize="small" />}
              fullWidth
              href={`/guide/thread/${currentThreadUuid}`}
              size="medium"
              variant="outlined"
              onClick={handleVeloQAContextPrep}
            >
              {ctaText}
            </Styled.VeloButton>
          </Styled.CTAFooter>
        )}
        <Box display="flex" justifyContent="center" sx={{ width: '100%', margin: 'auto' }}>
          <LegalFooter />
        </Box>
      </Styled.CardContainer>
    );
  }
);

VeloQA.propTypes = {
  configKey: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  prompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    })
  ),
  disabled: PropTypes.bool,
  showCTA: PropTypes.bool,
  ctaText: PropTypes.string,
  onThreadCreated: PropTypes.func,
  onResponseReceived: PropTypes.func,
  allowedComponentTypes: PropTypes.arrayOf(PropTypes.string),
  autoSubmitPrompts: PropTypes.bool,
  tag: PropTypes.string,
  origination: PropTypes.string,
  initialState: PropTypes.shape({
    threadUuid: PropTypes.string, // TODO: WIll only work on valid one assistant message threads
    sourceComponent: PropTypes.string
  }),
  onStateChange: PropTypes.func,
  onNavigateToChat: PropTypes.func,
  onError: PropTypes.func
};

export default VeloQA;
